import React, { useEffect } from "react";
import Modal from "../Modal";

/** Example tiles in "What we analyze"; remainder shown as "+N more" below the grid. */
const PATTERN_DIMENSION_EXAMPLES = [
  { label: "Content Type", hint: "Structure and style" },
  { label: "Hook Phrase", hint: "How the video opens" },
  { label: "Content Topic", hint: "What it is about" },
];

const PATTERN_EXAMPLE_BOX_COUNT = PATTERN_DIMENSION_EXAMPLES.length;

const dimensionTileClass =
  "flex flex-col rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-left min-h-[4.75rem] justify-center";

const PatternsInfoModal = ({ isOpen, onDismiss, totalPatternTypesCount = 0 }) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onDismiss]);

  if (!isOpen) return null;

  const morePatternsBeyondExamples = Math.max(0, totalPatternTypesCount - PATTERN_EXAMPLE_BOX_COUNT);

  return (
    <Modal onClose={onDismiss} maxWidth="max-w-xl" heightClass="max-h-[90vh] flex flex-col overflow-hidden" noPadding>
      <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1 min-h-0 space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-theme-text mb-1">How audience patterns work</h2>
          {/* <p className="text-sm text-theme-text opacity-70">
            Quick guide to what you are seeing in this table and what to do next.
          </p> */}
        </div>

        <section className="rounded-lg border border-theme-secondary bg-theme-secondary p-4">
          <h3 className="text-sm font-semibold text-theme-text mb-3 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-theme-main text-theme-primary border border-theme-secondary">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75A2.25 2.25 0 0115.75 13.5H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25zM13.5 6A2.25 2.25 0 0115.75 3.75H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25A2.25 2.25 0 0113.5 8.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25v-2.25z"
                />
              </svg>
            </span>
            What we analyze
          </h3>
          <p className="text-sm text-theme-text opacity-75 mb-3">
            We group videos in this audience into patterns you can explore. Examples of dimensions include:
          </p>
          <div className="grid grid-cols-3 gap-2">
            {PATTERN_DIMENSION_EXAMPLES.map((dim) => (
              <div key={dim.label} className={dimensionTileClass}>
                <span className="text-xs font-medium text-theme-text">{dim.label}</span>
                <span className="text-[11px] text-theme-text opacity-55 leading-tight mt-0.5">{dim.hint}</span>
              </div>
            ))}
          </div>
          {morePatternsBeyondExamples > 0 ? (
            <p className="text-center text-xs text-theme-text opacity-50 mt-3 tabular-nums">
              +{morePatternsBeyondExamples} more
            </p>
          ) : totalPatternTypesCount > 0 && totalPatternTypesCount <= PATTERN_EXAMPLE_BOX_COUNT ? (
            <p className="text-center text-xs text-theme-text opacity-50 mt-3">
              All pattern types for this project are listed in the sidebar
            </p>
          ) : null}
        </section>

        <section className="rounded-lg border border-theme-secondary bg-theme-secondary p-4">
          <h3 className="text-sm font-semibold text-theme-text mb-3 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-theme-main text-theme-primary border border-theme-secondary">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
                />
              </svg>
            </span>
            How to read the columns
          </h3>
          <div className="rounded-md border border-theme-secondary bg-theme-main overflow-hidden mb-4">
            <div className="grid grid-cols-3 gap-px bg-theme-secondary text-[10px] sm:text-xs font-medium text-theme-text uppercase tracking-wide">
              <div className="bg-theme-secondary px-2 py-1.5 text-center">Count</div>
              <div className="bg-theme-secondary px-2 py-1.5 text-center">Views</div>
              <div className="bg-theme-secondary px-2 py-1.5 text-center">Outlier</div>
            </div>
            <div className="grid grid-cols-3 gap-px bg-theme-secondary text-xs text-theme-text">
              <div className="bg-theme-main px-2 py-2 text-center opacity-80">12</div>
              <div className="bg-theme-main px-2 py-2 text-center opacity-80">48K</div>
              <div className="bg-theme-main px-2 py-2 text-center text-theme-primary font-medium">+32%</div>
            </div>
          </div>
          <ul className="space-y-3 text-sm text-theme-text">
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded bg-theme-main border border-theme-secondary text-xs font-semibold text-theme-primary">
                #
              </span>
              <span>
                <span className="font-medium text-theme-text">Count</span>
                <span className="text-theme-text opacity-70"> is how often this pattern shows up. Higher usually means it is more common in the niche.</span>
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded bg-theme-main border border-theme-secondary">
                <svg className="h-3.5 w-3.5 text-theme-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </span>
              <span>
                <span className="font-medium text-theme-text">Views</span>
                <span className="text-theme-text opacity-70"> is roughly how much these videos tend to get watched on average (median in the pattern).</span>
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded bg-theme-main border border-theme-secondary">
                <svg className="h-3.5 w-3.5 text-theme-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </span>
              <span>
                <span className="font-medium text-theme-text">Outlier</span>
                <span className="text-theme-text opacity-70">
                  {` is impact per creator: for each account, how videos in this pattern usually perform compared to that creator's other videos. Strong outliers can signal a format that punches above their baseline.`}
                </span>
              </span>
            </li>
          </ul>
        </section>

        <section className="rounded-lg border-2 border-theme-primary bg-theme-primary/10 p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-theme-primary text-theme-button">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                />
              </svg>
            </span>
            <div>
              <h3 className="text-sm font-semibold text-theme-text mb-1">Generate script ideas for high-performing patterns</h3>
              <p className="text-sm text-theme-text opacity-80 leading-relaxed">
                Click any row to see a full analysis: how the pattern performs, and why it works for viewers. From there you can
                generate content ideas tailored to your brand, which is the best next step once something looks promising.
              </p>
            </div>
          </div>
        </section>
      </div>

      <div className="px-6 py-4 border-t border-theme-secondary flex justify-end flex-shrink-0 bg-theme-main">
        <button
          type="button"
          onClick={onDismiss}
          className="px-5 py-2.5 text-sm font-medium rounded-lg bg-theme-primary text-theme-button hover:opacity-90 transition-opacity"
        >
          Got it
        </button>
      </div>
    </Modal>
  );
};

export default PatternsInfoModal;
